import { emptyReturnBoardListQuerySchema, type EmptyReturnBoardItem, type EmptyReturnBoardListQuery, type Page } from '@chukua/common';
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../../shared/shared.api';
import { toListParams } from '../../../shared/policies/list-params';
import { useListQuery } from '../../../shared/hooks/use-list-query';
import { listDeadlines } from '../deadlines.api';

/** `queryKey` records which query the loaded page answers, so a newer query shows as refreshing over the old rows. */
export type DeadlinesBoardState =
  { status: 'loading' } | { status: 'loaded'; page: Page<EmptyReturnBoardItem>; queryKey: string } | { status: 'forbidden' } | { status: 'error' };

const keyOf = (query: EmptyReturnBoardListQuery) => toListParams(query).toString();

function toErrorState(err: unknown): DeadlinesBoardState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/** The page of containers the URL's list query asks for (`useListQuery`), re-fetched whenever that query changes or `reload` is called. */
export function useDeadlinesBoard() {
  const list = useListQuery(emptyReturnBoardListQuerySchema);
  const { query } = list;
  const [state, setState] = useState<DeadlinesBoardState>({ status: 'loading' });
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    let cancelled = false;

    listDeadlines(query)
      .then((page) => {
        if (!cancelled) setState({ status: 'loaded', page, queryKey: keyOf(query) });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, [query, reloads]);

  const reload = useCallback(() => setReloads((count) => count + 1), []);
  const isRefreshing = state.status === 'loaded' && state.queryKey !== keyOf(query);

  return { state, list, isRefreshing, reload };
}
